import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logUxEvent } from './telemetry/uxLogger';
import ProgressBar from './components/ProgressBar';
import FormBackButton from './components/FormBackButton';
import DecoDivider from './components/DecoDivider';
import useViewportInsets from './hooks/useViewportInsets';
import useFormSubmitGuard from './hooks/useFormSubmitGuard';
import { formatCpf, isValidCpf } from './utils/patientContactValidation';
import {
  markSignupFieldTyped,
  readSignupFieldTypedHere,
} from './utils/signupFieldPrefill';
import { persistIdentityKey } from './utils/identityBackup';
import { persistInstantCouponAb } from './InstantCouponOffer';
import { setFlowVariant } from './utils/flowVariant';
import {
  nextSignupRoute,
  prevReachableSignupRoute,
  getSignupVariant,
  isFirstStep,
} from './utils/signupOrderAb';
import { ensureSignupSession } from './utils/ensureSignupSession';
import { takeSignupQuickResumeStep, cancelSignupQuickResume } from './utils/signupQuickResume';
import { awaitPendingRegistrationStart, currentSignupNavGeneration } from './utils/emailStepRegistration';
import { usesGuidedCopy, signupUxProgress } from './utils/signupUxAb';
import { signupCta, signupStepTitle } from './utils/signupUxCopy';
import FieldValidCheck, { FIELD_VALID_CHECK_CSS } from './components/FieldValidCheck';
import SignupGreeting, { SIGNUP_GREETING_CSS, readSignupFullName } from './components/SignupGreeting';
import SignupStepHeading from './components/SignupStepHeading';

const CpfForm = () => {
  const navigate = useNavigate();
  useViewportInsets();

  // Voltar no funil relê o CPF que esta aba digitou; consulta nova começa vazia.
  const [cpf, setCpf] = useState(() => {
    const cached = readSignupFieldTypedHere('patientCpf');
    return isValidCpf(cached) ? formatCpf(cached) : '';
  });
  const [touched, setTouched] = useState(false);
  const inputRef = useRef(null);

  const rawDigits = cpf.replace(/\D/g, '');
  const isValid = isValidCpf(rawDigits);
  const guided = usesGuidedCopy();

  const handleConfirmRaw = async () => {
    const navGen = currentSignupNavGeneration();
    setTouched(true);
    if (!isValid) {
      logUxEvent('cpf_validation_error', {
        reason: 'invalid_cpf',
        length: rawDigits.length,
      });
      return false; // libera o guard imediatamente
    }

    sessionStorage.setItem('patientCpf', rawDigits);
    localStorage.setItem('patientCpf', rawDigits);
    // Backup multi-camada (IDB + cookie) — defesa contra perda de LS.
    try { persistIdentityKey('patientCpf', rawDigits); } catch {}
    markSignupFieldTyped('patientCpf');
    logUxEvent('cpf_confirmed', { length: rawDigits.length });

    const session = await ensureSignupSession().catch(() => null);
    const patientId =
      (session && session.patientId) ||
      sessionStorage.getItem('patientId') ||
      localStorage.getItem('patientId') ||
      '';
    const patientEmail = sessionStorage.getItem('patientEmail') || localStorage.getItem('patientEmail') || '';
    const patientName = sessionStorage.getItem('patientName') || localStorage.getItem('patientName') || '';
    const url = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/+$/, '');

    if (patientId) {
      // Persiste CPF no funil; não bloqueia navegação se falhar.
      // A resposta traz o braço do A/B de fluxo quando o paciente é elegível
      // (primeiro cliente). Guardamos assim que chega — há três telas até a
      // triagem, tempo de sobra; se não chegar, a triagem assume o controle.
      let stepPatientId = patientId;
      awaitPendingRegistrationStart()
        .then(() => {
          stepPatientId = sessionStorage.getItem('patientId') || patientId;
          return fetch(`${url}/registration/step`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ patientId: stepPatientId, step: 'cpf', cpf: rawDigits }),
          });
        })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data && data.flowVariant) setFlowVariant(data.flowVariant, stepPatientId);
        })
        .catch(() => {});
    }

    // A/B cupom instantâneo pós-CPF (~15% entram; controle segue sem tela).
    // Timeout curto: se o backend atrasar, o funil não trava.
    let offer = null;
    if (patientId) {
      try {
        const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timer = ctrl ? setTimeout(() => ctrl.abort(), 2500) : null;
        const res = await fetch(`${url}/coupons/instant-ab/assign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            patientId,
            cpf: rawDigits,
            ...(patientEmail ? { email: patientEmail } : {}),
            ...(patientName ? { fullName: patientName } : {}),
          }),
          ...(ctrl ? { signal: ctrl.signal } : {}),
        });
        if (timer) clearTimeout(timer);
        const data = await res.json().catch(() => ({}));
        if (data && data.enrolled && data.variant === 'coupon' && data.couponCode) {
          offer = {
            couponCode: data.couponCode,
            priceCents: data.priceCents,
            amount: data.amount,
            couponExpiresMs: data.couponExpiresMs || null,
          };
          persistInstantCouponAb(offer);
          logUxEvent('instant_coupon_assigned', {
            priceCents: offer.priceCents || null,
          });
        } else if (data && data.enrolled && data.variant === 'control') {
          logUxEvent('instant_coupon_control', {});
        }
      } catch {
        // Fail-open: segue o funil sem cupom
      }
    }

    if (navGen !== currentSignupNavGeneration()) return true;

    if (offer) {
      // A tela de cupom é uma parada opcional entre `cpf` e o próximo passo do
      // braço. Ela lê `getSignupVariant()` para decidir seu próprio destino.
      navigate('/cupom-primeira', { state: { offer } });
    } else {
      const variant = getSignupVariant();
      const dest = nextSignupRoute('cpf', variant) || '/telefone';
      navigate(dest);
    }
    return true;
  };

  // Cooldown maior: assign do A/B pós-CPF pode levar até ~2,5s.
  const [isSubmitting, handleConfirm] = useFormSubmitGuard(handleConfirmRaw, { cooldownMs: 3500 });

  // "Continuar como" na tela do nome: CPF salvo confirma sozinho.
  useEffect(() => {
    if (!takeSignupQuickResumeStep('cpf')) return;
    if (!isValid) {
      cancelSignupQuickResume();
      return;
    }
    logUxEvent('signup_quick_resume_step', { step: 'cpf' });
    handleConfirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const formatted = formatCpf(e.target.value);
    setCpf(formatted);
    if (!touched) setTouched(true);
  };

  const getErrorMessage = () => {
    if (!touched) return ' ';
    if (!rawDigits) return 'Informe seu CPF.';
    if (rawDigits.length < 11) return 'Faltam dígitos para completar o CPF.';
    if (!isValid) return 'CPF inválido. Verifique os números informados.';
    return ' ';
  };

  return (
    <>
      <style>{`
        ${FIELD_VALID_CHECK_CSS}
        ${SIGNUP_GREETING_CSS}
        /* Poppins loaded globally in index.html */

        :root {
          --primary: #D32F2F;
          --bg-page: #F9FAFB;
          --surface: #FFFFFF;
          --text-main: #1E1E1E;
          --text-dim: #6B6B6B;
          --border-subtle: #E5E7EB;
          --error: #D32F2F;
          --radius-lg: 1rem;
          --radius-xl: 1.5rem;
          --radius-full: 999px;
          --shadow-xs: 0 1px 2px rgba(0,0,0,0.04);
          --shadow-sm: 0 6px 16px rgba(0,0,0,0.06);
          --shadow-lg: 0 12px 28px rgba(0,0,0,0.08);
        }

        body {
          margin: 0;
          background: var(--bg-page);
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Inter', 'Roboto', sans-serif;
          color: var(--text-main);
          -webkit-font-smoothing: antialiased;
        }

        .page-shell {
          min-height: 100vh;
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          background: var(--bg-page);
        }

        /* padding-bottom usa --vvb (altura do teclado virtual quando aberto)
           e env(safe-area-inset-bottom) para garantir que o botão Continuar
           nunca fique escondido atrás do teclado em iOS/Android. */
        .inner-wrap {
          flex: 1;
          max-width: 480px;
          width: 100%;
          margin: 0 auto;
          padding: 4.5rem 1rem max(2rem, calc(1rem + var(--vvb, 0px) + env(safe-area-inset-bottom, 0px)));
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 1.25rem;
          box-sizing: border-box;
          transition: padding-bottom 0.18s ease;
        }

        .text-field {
          scroll-margin-bottom: calc(160px + env(safe-area-inset-bottom, 0px));
          scroll-margin-top: 1rem;
        }

        .header-block {
          text-align: center;
          max-width: 22rem;
          margin-top: 1rem;
          margin-bottom: 2rem;
        }

        .main-headline {
          font-size: 1.25rem;
          line-height: 1.35;
          font-weight: 600;
          color: var(--text-main);
          margin: 0 0 .75rem;
        }

        .sub-headline {
          font-size: .9rem;
          line-height: 1.4;
          font-weight: 400;
          color: var(--text-dim);
          margin: 0 auto;
        }

        .form-card {
          width: 100%;
          max-width: 400px;
          background: var(--surface);
          border-radius: var(--radius-xl);
          box-shadow: var(--shadow-sm);
          padding: 1.25rem 1.25rem 1rem;
          border: 1px solid rgba(0,0,0,0.06);
          display: flex;
          flex-direction: column;
          align-items: stretch;
          box-sizing: border-box;
        }

        .field-helper {
          font-size: .75rem;
          color: var(--text-dim);
          line-height: 1.4;
          margin-top: .55rem;
          margin-bottom: .25rem;
        }

        .label-row {
          display: flex;
          flex-direction: column;
          margin-bottom: .75rem;
        }

        .label-title {
          font-size: .8rem;
          font-weight: 500;
          line-height: 1.3;
          color: var(--text-main);
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          width: 100%;
          box-sizing: border-box;
        }

        .label-hint {
          font-size: .7rem;
          font-weight: 400;
          color: var(--text-dim);
          line-height: 1.3;
        }

        .field-wrapper {
          width: 100%;
          background: var(--surface);
          border-radius: var(--radius-lg);
          border: 1px solid var(--border-subtle);
          box-shadow: none;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          padding: 0;
          overflow: hidden;
          transition: border-color .18s ease, box-shadow .18s ease, background .18s ease;
        }

        .text-field {
          flex: 1 1 auto;
          width: auto;
          min-width: 0;
          border: none;
          outline: none;
          font-size: 1.05rem;
          line-height: 1.4;
          color: var(--text-main);
          background: transparent;
          padding: 1rem 1.1rem;
          box-sizing: border-box;
          -webkit-appearance: none;
        }

        .text-field:focus {
          outline: none;
        }

        .field-wrapper:focus-within {
          border: 2px solid var(--primary);
          box-shadow: 0 0 0 3px rgba(211,47,47,0.10);
          background: #FFF;
        }

        .error-text {
          min-height: 1.25rem;
          font-size: .8rem;
          line-height: 1.4;
          font-weight: 500;
          color: var(--error);
          margin-top: .6rem;
          margin-bottom: 1rem;
        }

        .confirm-wrap {
          display: flex;
          justify-content: center;
          margin-top: .5rem;
          margin-bottom: .5rem;
        }

        .confirm-button {
          background: var(--primary);
          color: #FFF;
          border: none;
          border-radius: var(--radius-full);
          box-shadow: none;
          font-size: 1.05rem;
          line-height: 1.2;
          font-weight: 600;
          padding: 1rem 1.5rem;
          min-width: 200px;
          min-height: 48px;
          cursor: pointer;
          transition: background .18s ease, opacity .18s ease;
          -webkit-tap-highlight-color: transparent;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: .5rem;
        }
        .confirm-button__spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          border-radius: 50%;
          animation: cf-spin 0.8s linear infinite;
        }
        @keyframes cf-spin { to { transform: rotate(360deg); } }
        .confirm-button:hover:not(:disabled) {
          background: #B71C1C;
        }
        .confirm-button:focus-visible {
          box-shadow: 0 0 0 3px rgba(211,47,47,0.12);
        }
        .confirm-button:disabled {
          opacity: .6;
          cursor: not-allowed;
          box-shadow: var(--shadow-xs);
        }
        .confirm-button:active:not(:disabled) {
          transform: scale(.98);
          box-shadow: var(--shadow-sm);
        }

        .contact-link {
          margin-top: auto;
          text-align: center;
          font-size: .8rem;
          line-height: 1.4;
          color: var(--text-main);
          font-weight: 400;
          padding: 0 1rem calc(1.5rem + env(safe-area-inset-bottom));
          box-sizing: border-box;
        }

        .contact-link a {
          color: var(--primary);
          font-weight: 600;
          text-decoration: none;
        }

        @media (min-width: 480px) {
          .inner-wrap {
            padding: 5.5rem 2rem 4rem;
          }
          .main-headline {
            font-size: 1.4rem;
          }
          .sub-headline {
            font-size: .9rem;
          }
          .confirm-button {
            min-width: 220px;
            font-size: 1rem;
          }
        }

        @media (min-width: 768px) {
          .main-headline {
            font-size: 1.5rem;
          }
          .sub-headline {
            font-size: .9rem;
          }
        }
      `}</style>

      <div className="page-shell">
        <div className="inner-wrap">
          <FormBackButton
            onClick={() => {
              const variant = getSignupVariant();
              if (isFirstStep('/cpf', variant)) {
                navigate('/');
                return;
              }
              const prev = prevReachableSignupRoute('cpf', variant);
              navigate(prev || '/email');
            }}
          />

          <ProgressBar {...signupUxProgress('/cpf', getSignupVariant())} />

          <div className="header-block">
            {guided ? <SignupGreeting fullName={readSignupFullName()} /> : null}
            <h1 className="main-headline deco-headline">Qual é o CPF do paciente?</h1>
            <DecoDivider variant="chevron" />
          </div>

          <form
            className="form-card"
            onSubmit={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            noValidate
          >
            {guided ? <SignupStepHeading title={signupStepTitle('/cpf')} /> : null}
            <div className="label-row">
              <div className="label-title">
                <label htmlFor="patient-cpf">CPF</label>
              </div>
              <div
                className="label-hint"
                id="patient-cpf-hint"
                onClick={() => { try { document.getElementById('patient-cpf')?.focus(); } catch {} }}
              >Ex.: "000.000.000-00"</div>
            </div>

            <div className="field-wrapper deco-field-focus">
              <input
                id="patient-cpf"
                ref={inputRef}
                type="text"
                inputMode="numeric"
                className="text-field"
                placeholder="000.000.000-00"
                value={cpf}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleConfirm();
                  }
                }}
                onFocus={(e) => {
                  const node = e.currentTarget;
                  setTimeout(() => {
                    try { node.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch {}
                  }, 220);
                }}
                maxLength={14}
                autoComplete="off"
                pattern="[0-9.\-]*"
                spellCheck={false}
                enterKeyHint="next"
                aria-invalid={touched && !isValid}
                aria-describedby="patient-cpf-hint patient-cpf-helper patient-cpf-error"
                aria-required="true"
              />
              {guided && <FieldValidCheck show={isValid} label="CPF válido" />}
            </div>

            <div className="field-helper" id="patient-cpf-helper">
              Usamos só para emitir suas receitas e atestados.
            </div>

            <div className="error-text" id="patient-cpf-error" role={touched && !isValid ? 'alert' : undefined}>
              {getErrorMessage()}
            </div>

            <div className="confirm-wrap">
              <button
                type="submit"
                className="confirm-button"
                disabled={!isValid || isSubmitting}
              >
                {signupCta('cpf', { guided, submitting: isSubmitting })}
              </button>
            </div>
          </form>

        </div>
      </div>
    </>
  );
};

export default CpfForm;
